import { uniqueId } from 'lodash';

import { DEFAULT_CORNER_RADIUS } from '@common/constants';
import { ProductType, ShapeType, containsBumpOut, containsRadiusOut } from '@common/enums';
import { MathFunctions, getSideItemJointPoints } from '@common/helpers';
import { getSegmentJointPoints } from '@common/helpers/joint-point-helpers';
import {
    ApplianceType,
    ArrowInfo,
    ColorModel,
    DrawingEdge,
    DrawingShape,
    MaterialModel,
    PricedProduct,
    ProductCatalogMap,
    ProductEstimateQuery,
    ProductQty,
} from '@common/models';
import { SaveEstimateItem } from '@common/models/save-estimate-dto';
import { buildQteAttributes, getProductDescription, isActualProduct } from '@common/utils';

export const lineItemsFromPricedProducts = (pricedProducts: PricedProduct[]): SaveEstimateItem[] =>
    Array.from(
        pricedProducts
            .filter((product) => isActualProduct(product.productId) && product.adjustedQty > 0)
            .reduce((accumulator, product) => {
                accumulator.set(product.productId, (accumulator.get(product.productId) ?? 0) + product.adjustedQty);
                return accumulator;
            }, new Map()),
        ([productId, quantity]) => ({ productId, quantity })
    );

/**
 * Filter out products we already have known prices for and use those known prices if present
 * unknownProductsQuery = query details for products that are not in our catalog (should be very rare)
 * completedPricedProducts = fully PricedProducts based on existing catalog data
 *
 * @param productEstimateQuery
 * @returns [unknownProductsQuery, completedPricedProducts]
 */
export const getPricingEstimate = ({
    productCatalog,
    productEstimateQuery,
    selectedColor,
}: {
    productCatalog: ProductCatalogMap;
    productEstimateQuery: ProductEstimateQuery;
    selectedColor?: ColorModel;
}): [ProductEstimateQuery, PricedProduct[]] => {
    const initialUnknownProductsQuery: ProductEstimateQuery = {
        zipCode: productEstimateQuery.zipCode,
        storeId: productEstimateQuery.storeId,
        products: [],
    };

    return productEstimateQuery.products.reduce<[ProductEstimateQuery, PricedProduct[]]>(
        ([unknownProductsQuery, completedPricedProducts], product) => {
            const catalogItem = productCatalog.get(product.productId);
            if (catalogItem) {
                // look for a promo for this product that has no color-specific-requirement
                const freeItemPromo = catalogItem.promos?.find((promo) =>
                    promo.colorProductIds
                        ? promo.colorProductIds
                              .split(',')
                              .some((promoColorId) => Number(promoColorId) === selectedColor?.productId)
                        : true
                );

                // if there was a promo found, adjust quantity that's ONLY used for price calculations
                const adjustedQty = freeItemPromo
                    ? Math.max(0, product.quantity - freeItemPromo.maxQty)
                    : product.quantity;

                const existingPricedProduct: PricedProduct = {
                    ...catalogItem,
                    unitPrice: catalogItem.price,
                    unitPriceWithoutDiscount: catalogItem.priceWithoutDiscount,
                    lineItemId: product.lineItemId,
                    listKey: uniqueId(), // TODO is this needed/correct?
                    price: catalogItem.price * adjustedQty,
                    priceWithoutDiscount: catalogItem.priceWithoutDiscount * adjustedQty,
                    quantity: product.quantity,
                    adjustedQty,
                };
                completedPricedProducts.push(existingPricedProduct);
            } else if (isActualProduct(product.productId)) {
                unknownProductsQuery.products.push(product);
            }
            return [unknownProductsQuery, completedPricedProducts];
        },
        [initialUnknownProductsQuery, []]
    );
};

/**
 * true if the adjacent edges for the given corner are both finished edges
 * @param shape
 * @param cornerID
 */
const isCornerAFinishedEdge = (shape: DrawingShape, cornerID: string): boolean => {
    if (!shape.arrowsInfo || !shape.drawingEdges) {
        return false;
    }
    // get the IDs of the edges adjacent to the corner
    const edgeIds = shape.drawingEdges
        .filter((e) => e.startCornerId === cornerID || e.endCornerId === cornerID)
        .map((e) => e.id);

    // find the arrowsInfo objects that are associated with those edges
    const arrows = shape.arrowsInfo.filter((a) => edgeIds.includes(a.drawingEdgeId || ''));

    // if either of the two adjacent edges is unfinished, then return false
    return !arrows.some((a) => !a.isActiveForEdgeCalc);
};

export const getEdgeQuantity = (drawingShape: DrawingShape): number => {
    const edgesQuantity = (drawingShape.arrowsInfo ?? [])
        .filter((arrow: ArrowInfo) => arrow.size && !arrow.isInterior && arrow.isActiveForEdgeCalc)
        .reduce((edgesQuantity, arrow: ArrowInfo) => {
            const arrowsEdge = drawingShape.drawingEdges.filter((edge: DrawingEdge) => arrow.drawingEdgeId === edge.id);
            if (arrowsEdge[0] && containsRadiusOut(arrowsEdge[0])) {
                return (
                    edgesQuantity + MathFunctions.calculateArcLength(arrow.size ?? 0, arrowsEdge[0].radiusSagitta ?? 0)
                );
            } else if (arrowsEdge[0] && containsBumpOut(arrowsEdge[0])) {
                return edgesQuantity + arrow.size! + arrowsEdge[0].bumpOut!.depth! * 2;
            } else {
                return edgesQuantity + (arrow.size ?? 0);
            }
        }, 0);

    // adjust the edge length total by subtracting out any radius corners straight edges and adding in the rounded length
    const defaultAngle = 90;

    const _edgesQuantity = Object.values(drawingShape.cornerMap).reduce((edgesQuantity, cornerRadius) => {
        if (
            cornerRadius.productId !== 0 &&
            cornerRadius.size !== DEFAULT_CORNER_RADIUS &&
            isCornerAFinishedEdge(drawingShape, cornerRadius.id) &&
            !cornerRadius.diagonalLength
        ) {
            // TODO Below lay the formulas that will rid us of the MAGIC_NUMBER constants at some point.
            // the angle of the corner's circle will be different likely than the angle of the corner itself
            const angleOfCircleInDegrees = 2 * (90 - Math.abs((cornerRadius?.angle || defaultAngle) / 2));
            const cornerEdgeLength = (2 * Math.PI * Math.floor(cornerRadius.size || 0) * angleOfCircleInDegrees) / 360;
            const angleOfCornerInRadians = ((cornerRadius?.angle ?? defaultAngle) * Math.PI) / 180;
            const removedStraightEdgeLength = Math.floor(cornerRadius.size || 0) / Math.tan(angleOfCornerInRadians / 2);
            return edgesQuantity + Math.abs(cornerEdgeLength) - 2 * Math.abs(removedStraightEdgeLength);
        }
        return edgesQuantity;
    }, edgesQuantity);

    return Math.ceil(_edgesQuantity);
};

export const getProductTypeFromApplianceType = (appliance: ApplianceType): ProductType => {
    const mapApplianceTypeToProductType: Record<ApplianceType, ProductType> = {
        [ApplianceType.DISHWASHER]: ProductType.DISHWASHER,
        [ApplianceType.COOKTOP]: ProductType.COOKING_APPLIANCE,
        [ApplianceType.RANGE]: ProductType.COOKING_APPLIANCE,
        [ApplianceType.REFRIGERATOR]: ProductType.REFRIGERATOR,
        [ApplianceType.FAUCET]: ProductType.FAUCET,
        [ApplianceType.SINK]: ProductType.SINK,
        [ApplianceType.SINK_LABOR]: ProductType.SINK_LABOR,
    };

    return mapApplianceTypeToProductType[appliance];
};

/**
 * Accumulate duplicate corner radii
 */
const duplicateProductsReducer = (accumulatedPricedProducts: PricedProduct[], currentPricedProduct: PricedProduct) => {
    const duplicatePricedProduct = accumulatedPricedProducts.findIndex(
        (pricedProduct) =>
            pricedProduct.productId === currentPricedProduct.productId && !currentPricedProduct.lineItemId
    );

    if (duplicatePricedProduct !== -1) {
        accumulatedPricedProducts[duplicatePricedProduct] = {
            ...accumulatedPricedProducts[duplicatePricedProduct],
            quantity: accumulatedPricedProducts[duplicatePricedProduct].quantity + currentPricedProduct.quantity,
        };
    } else {
        accumulatedPricedProducts.push(currentPricedProduct);
    }
    return accumulatedPricedProducts;
};

/**
 * Aggregate data from request and client side
 */
export const getAggregatedPricedProducts = ({
    drawingShapes,
    pricedProducts,
    selectedColor,
    selectedMaterial,
}: {
    drawingShapes: DrawingShape[];
    pricedProducts: PricedProduct[];
    selectedColor?: ColorModel;
    selectedMaterial?: MaterialModel;
    //eslint-disable-next-line  sonarjs/cognitive-complexity
}): PricedProduct[] => {
    const aggregatedPricedProducts = [...pricedProducts];

    if (selectedMaterial && selectedColor?.jointProductId) {
        const jointPoint = selectedMaterial.jointPoints.find(
            (jointPoint) => selectedColor.jointProductId && jointPoint.productId === selectedColor.jointProductId
        );

        if (jointPoint && jointPoint.productId) {
            // pre-compute the total quantity and then add just one product line
            const jointPointQuantity = drawingShapes.reduce((acc: number, drawingShape: DrawingShape) => {
                const segmentJointPoints = getSegmentJointPoints(drawingShape, selectedMaterial);
                const sideItemJointPoints = getSideItemJointPoints(drawingShape, selectedMaterial);

                return acc + drawingShape.jointPointsCount + segmentJointPoints + sideItemJointPoints;
            }, 0);
            const product: PricedProduct = {
                unitPrice: jointPoint.price,
                unitPriceWithoutDiscount: jointPoint.priceWithoutDiscount,
                abbrevDesc: jointPoint.abbrevDesc,
                adjustedQty: jointPointQuantity,
                consumerDesc: jointPoint.consumerDesc,
                listKey: jointPoint?.productId?.toString(),
                name: getProductDescription(jointPoint),
                price: jointPoint.price * jointPointQuantity,
                priceWithoutDiscount: jointPoint.priceWithoutDiscount * jointPointQuantity,
                productId: jointPoint.productId,
                productType: ProductType.JOINT_POINT,
                quantity: jointPointQuantity,
                unitOfMeasure: jointPoint.unitOfMeasure,
                masterSku: jointPoint.masterSku,
                ...buildQteAttributes(jointPoint),
            };
            aggregatedPricedProducts.push(product);
        }
    }

    const reducedAggregatedPricedProducts: PricedProduct[] = aggregatedPricedProducts.reduce(
        duplicateProductsReducer,
        []
    );
    // send the list ordered by productId
    return reducedAggregatedPricedProducts
        .filter((product) => product.quantity > 0)
        .sort((prevPricedProduct, currentPricedProduct) => {
            if (
                prevPricedProduct.productType === ProductType.CORNER_RADIUS &&
                currentPricedProduct.productType === ProductType.CORNER_RADIUS
            ) {
                if (
                    prevPricedProduct.productId &&
                    currentPricedProduct.productId &&
                    prevPricedProduct.productId > currentPricedProduct.productId
                ) {
                    return 1;
                }
                return -1;
            }
            return 0;
        });
};

// TODO: type drawingShapes correctly
export const buildEdgesPrice = (drawingShapes: DrawingShape[]): ProductQty[] => {
    const edges: ProductQty[] = [];

    drawingShapes.forEach((drawingShape: DrawingShape) => {
        if (drawingShape.edge === undefined || drawingShape.type === ShapeType.FAUX_SHAPE) {
            return;
        }
        const edgeQuantity = getEdgeQuantity(drawingShape);
        const existingEdge = edges.find((existingEdge) => existingEdge.productId === drawingShape.edge?.productId);

        if (existingEdge !== undefined) {
            existingEdge.quantity += edgeQuantity;
        } else {
            edges.push({
                productId: drawingShape.edge.productId!,
                quantity: edgeQuantity,
            });
        }
    });
    return edges;
};

export const _updatePrevCounterTopAreaQty = (prevCounterTopAreaQty: Set<number>, actionPayload: number): Set<number> =>
    new Set(
        prevCounterTopAreaQty.size > 1
            ? [...prevCounterTopAreaQty, actionPayload].slice(1)
            : [...prevCounterTopAreaQty, actionPayload]
    );
